import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { environment } from '@tytapp/environment';
import { rewriteUrl } from '@tytapp/environment-utils';
import { Subscription } from 'rxjs';

@Component({
  selector: 'tyt-new-url-field',
  standalone: false,
  template: `
    <div class="field-container">
      <div class="field-row">
        <mat-form-field appearance="outline" floatLabel="always" class="flex-grow">
          <mat-label>{{ mode === 'url' ? label : 'Recipient Email' }}</mat-label>

          @if (mode === 'url') {
            <input matInput type="url" [formControl]="urlFormControl" placeholder=" " [disabled]="disabled">
          }
          @if (mode === 'mailto') {
            <input matInput type="email" [formControl]="emailFormControl" placeholder=" " [disabled]="disabled">
          }

          <mat-hint>
            @if (mode === 'url' && urlFormControl.value && urlFormControl.valid) {
              Make sure it works! <a [href]="rewrittenUrl" target="_blank">Click here</a> to test link in new tab
            } @else if (mode === 'mailto') {
                Enter one or more emails, separated by commas.
            }
          </mat-hint>
          @if (isInputInvalid()){
          <mat-error>
            {{ errorMessage }}
          </mat-error>
            }
        </mat-form-field>

      <mat-form-field appearance="outline" floatLabel="always" class="w-40 flex-shrink-0">
        <mat-label>Link Type</mat-label>
        <mat-select [(ngModel)]="mode" (ngModelChange)="onModeChange()" [disabled]="disabled">
          <mat-option value="url">URL</mat-option>
          <mat-option value="mailto">Mailto</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

      @if (mode === 'mailto') {
        <div class="mail-container">
          <mat-form-field appearance="outline" floatLabel="always" class="w-full">
            <mat-label>Subject (Optional)</mat-label>
            <input matInput type="text" [(ngModel)]="subject" (ngModelChange)="onInputChange()" placeholder=" " [disabled]="disabled">
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always" class="w-full">
            <mat-label>Body (Optional)</mat-label>
            <textarea matInput rows="3" [(ngModel)]="body" (ngModelChange)="onInputChange()" placeholder=" " [disabled]="disabled"></textarea>
          </mat-form-field>
        </div>
      }
    </div>
  `,
  styles: [`
        .field-container {
            display: flex;
            flex-direction: column;
            width: 100%;
        }

        .field-row {
            display: flex;
            gap: 1rem;
            width: 100%;
        }

        .flex-grow {
            flex-grow: 1;
        }

        .form-container {
            width: 10rem;
        }

        .w-full {
            width: 100%;
        }

        .mail-container {
            margin-top: 1rem;
        }

        mat-hint {
            white-space: nowrap;
        }

        @media (max-width: 800px) {
            mat-hint {
              display: none;
            }
        }
  `]
})
export class NewUrlFieldComponent {
    @Input() label: string = 'Destination URL';
    @Input() hint: string = '';
    @Input() disabled: boolean = false;
    @Input() initialUrl: string = '';
    @Output() urlChange = new EventEmitter<string>();

    mode: 'url' | 'mailto' = 'url';
    urlValue: string = '';
    recipient: string = '';
    subject: string = '';
    body: string = '';
    generatedUrl: string = '';
    urlPattern = /^\/.*$|^https:\/\/([A-Za-z0-9-]+\.)+[A-Za-z0-9-]+($|\/.*$)/;
    emailPattern = /^(?:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:,\s*)?)*[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    urlFormControl = new FormControl(this.urlValue, [Validators.required, Validators.pattern(this.urlPattern)]);
    emailFormControl = new FormControl(this.recipient, [Validators.required, Validators.pattern(this.emailPattern)]);

    private subscriptions = new Subscription();

    ngOnInit(): void {
        this.detectInitialModeAndParseUrl(this.initialUrl);
        this.syncFormControlsWithComponentState();
        this.updateGeneratedUrl();

        this.subscriptions.add(
            this.urlFormControl.valueChanges.subscribe(value => {
                if (this.mode === 'url') {
                    this.urlValue = value || '';
                    this.updateGeneratedUrl();
                }
            })
        );
        this.subscriptions.add(
            this.emailFormControl.valueChanges.subscribe(value => {
                if (this.mode === 'mailto') {
                    this.recipient = value || '';
                    this.updateGeneratedUrl();
                }
            })
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialUrl'] && changes['initialUrl'].currentValue !== changes['initialUrl'].previousValue) {
            this.detectInitialModeAndParseUrl(changes['initialUrl'].currentValue);
            this.syncFormControlsWithComponentState();
            this.updateGeneratedUrl();
        }
        if (changes['disabled']) {
            if (this.disabled) {
                this.urlFormControl.disable({ emitEvent: false });
                this.emailFormControl.disable({ emitEvent: false });
            } else {
                this.urlFormControl.enable({ emitEvent: false });
                this.emailFormControl.enable({ emitEvent: false });
            }
        }
    }

    private syncFormControlsWithComponentState(): void {
        this.urlFormControl.setValue(this.urlValue, { emitEvent: false });
        this.emailFormControl.setValue(this.recipient, { emitEvent: false });

        if (this.mode === 'url') {
            this.urlFormControl.setValidators([Validators.required, Validators.pattern(this.urlPattern)]);
            this.urlFormControl.enable({ emitEvent: false });
            this.emailFormControl.clearValidators();
            this.emailFormControl.disable({ emitEvent: false });
        } else {
            this.emailFormControl.setValidators([Validators.required, Validators.pattern(this.emailPattern)]);
            this.emailFormControl.enable({ emitEvent: false });
            this.urlFormControl.clearValidators();
            this.urlFormControl.disable({ emitEvent: false });
        }
        this.urlFormControl.updateValueAndValidity();
        this.emailFormControl.updateValueAndValidity();
    }


    private detectInitialModeAndParseUrl(url: string): void {
        const safeUrl = url ?? '';

        if (safeUrl.startsWith('mailto:')) {
            this.mode = 'mailto';
            const mailtoParts = safeUrl.substring('mailto:'.length).split('?');
            this.recipient = decodeURIComponent(mailtoParts[0] || '');

            if (mailtoParts.length > 1) {
                const queryParams = new URLSearchParams(mailtoParts[1]);
                this.subject = decodeURIComponent(queryParams.get('subject') || '');
                this.body = decodeURIComponent(queryParams.get('body') || '');
            }
        } else {
            this.mode = 'url';
            this.urlValue = safeUrl; // Assign the safeurl directly
        }
    }

    onModeChange(): void {
        this.syncFormControlsWithComponentState();
        this.updateGeneratedUrl();
    }

    onInputChange(): void {
        this.updateGeneratedUrl();
    }

    private updateGeneratedUrl(): void {
        let newUrl = '';
        if (this.mode === 'url') {
            newUrl = this.urlValue;
        } else {
            newUrl = `mailto:${this.encodeMailtoParam(this.recipient)}`;
            const params = [];
            if (this.subject) params.push(`subject=${this.encodeMailtoParam(this.subject)}`);
            if (this.body) params.push(`body=${this.encodeMailtoParam(this.body)}`);
            if (params.length > 0) newUrl += `?${params.join('&')}`;
        }
        this.generatedUrl = newUrl;

        // workaround: this change is to fix NG0100 ***
        setTimeout(() => {
            this.urlChange.emit(this.generatedUrl);
        }, 0); // A delay of 0 milliseconds pushes it to the end of the current task queue
    }

    get rewrittenUrl() {
        if (this.mode === 'url' && this.urlFormControl.valid && this.urlFormControl.value) {
            return rewriteUrl(environment, this.urlFormControl.value);
        }
        return '';
    }

    private encodeMailtoParam(str: string): string {
        return encodeURIComponent(str);
    }

    isInputInvalid(): boolean {
        if (this.mode === 'url') {
            return this.urlFormControl.invalid && (this.urlFormControl.dirty || this.urlFormControl.touched);
        } else {
            return this.emailFormControl.invalid && (this.emailFormControl.dirty || this.emailFormControl.touched);
        }
    }

    get errorMessage(): string | null {
        if (this.mode === 'url') {
            if (this.urlFormControl.errors?.['required']) {
                return `Please enter a URL`;
            }
            if (this.urlFormControl.errors?.['pattern']) {
                return `Invalid URL format. URL must begin with "https://" or "/"`;
            }
        }

        if (this.mode === 'mailto') {
                if (this.emailFormControl.errors?.['required']) {
                    return `Recipient email is required`;
                }
                if (this.emailFormControl.errors?.['pattern']) {
                    return `Invalid email format. Use commas for multiple emails.`;
                }
            }
        return null;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}